import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconSearch, IconX, IconArrowRight, IconPlus, IconCornerDownLeft,
  IconArrowUp, IconArrowDown, IconCommand, IconBolt, IconAdjustments,
} from '@tabler/icons-react';
import ProductImage from './ProductImage';

/**
 * SearchModal — command-palette style product search for the POS.
 *
 * Opens from the search button in the toolbar (or `/` / `cmd+k`).
 * Live filters CATALOGUE as the cashier types. Keyboard:
 *   ↑ / ↓   move selection
 *   ⏎       open the selected product (modifier modal)
 *   ⌘⏎ / ↩  quick-add the selected product to cart at default size/qty 1
 *   esc     close
 *
 * Results render with the same category-coloured tile + eyebrow as
 * the main product grid so cashiers don't context-switch visually.
 *
 * Light + fast: simple substring + word-prefix fuzzy match (no
 * external lib). On a 200-product catalogue this is sub-millisecond.
 */

export interface SearchableItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  categoryLabel: string;
  categoryEyebrow: string;
  categoryTile: string;
  priceCents: number;
  originalPriceCents?: number;
  imageUrl?: string;
  badge?: string;
}

interface Props {
  open: boolean;
  items: SearchableItem[];
  onClose: () => void;
  /** Open the full modifier modal for this item (sizes, addons, etc.). */
  onOpenDetails: (item: SearchableItem) => void;
  /** Quick-add to cart at default size/qty 1. */
  onQuickAdd: (item: SearchableItem) => void;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

/**
 * Score a single item against the query.
 * Higher is better. 0 = no match.
 *
 *   exact prefix on name           → 100
 *   word-start prefix on name      →  80
 *   substring in name              →  60
 *   substring in category label    →  40
 *   substring in description       →  20
 */
function scoreItem(it: SearchableItem, q: string): number {
  if (!q) return 0;
  const ql = q.toLowerCase();
  const name = it.name.toLowerCase();
  if (name === ql) return 200;
  if (name.startsWith(ql)) return 100;
  if (name.split(/\s+/).some((w) => w.startsWith(ql))) return 80;
  if (name.includes(ql)) return 60;
  if (it.categoryLabel.toLowerCase().includes(ql)) return 40;
  if (it.description?.toLowerCase().includes(ql)) return 20;
  return 0;
}

export default function SearchModal({ open, items, onClose, onOpenDetails, onQuickAdd }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset on open + autofocus.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Delay so the focus lands after the modal mounts.
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Filtered + sorted results.
  const results = useMemo(() => {
    if (!query.trim()) {
      // Default state: surface a curated "popular" mini-set so the
      // modal isn't empty when the cashier opens it. Uses the first
      // 8 items as a stand-in until backend ships popularity data.
      return items.slice(0, 8);
    }
    return items
      .map((it) => ({ it, score: scoreItem(it, query.trim()) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((r) => r.it);
  }, [items, query]);

  // Clamp active when results shrink.
  useEffect(() => {
    if (active >= results.length) setActive(0);
  }, [results.length, active]);

  // Scroll the active row into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Body scroll lock + Esc handler.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      if (e.key === 'Enter') {
        const r = results[active];
        if (!r) return;
        e.preventDefault();
        if (e.metaKey || e.ctrlKey) onQuickAdd(r);
        else onOpenDetails(r);
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, results, active, onClose, onOpenDetails, onQuickAdd]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4 bg-black/55 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-surface rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search field */}
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <IconSearch size={22} stroke={2.25} className="text-primary flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            placeholder="Search products, categories, descriptions…"
            className="flex-1 h-10 bg-transparent outline-none text-16 font-semibold text-heading placeholder-gray-500"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="text-12 font-bold uppercase tracking-wider text-gray-500 hover:text-heading transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-light hover:text-heading transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <IconX size={18} stroke={2.25} />
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 max-h-[60vh] overflow-y-auto" ref={listRef}>
          {!query.trim() && (
            <p className="px-5 pt-4 pb-2 text-12 font-bold uppercase tracking-widest text-gray-500">
              Popular right now
            </p>
          )}
          {query.trim() && results.length > 0 && (
            <p className="px-5 pt-4 pb-2 text-12 font-bold uppercase tracking-widest text-gray-500">
              {results.length} match{results.length === 1 ? '' : 'es'}
            </p>
          )}

          {results.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-light inline-flex items-center justify-center text-gray-500 mb-3 border border-border">
                <IconSearch size={22} stroke={2} />
              </div>
              <p className="text-15 font-bold text-heading">No products match "{query}"</p>
              <p className="text-13 font-medium text-gray-700 mt-1.5">Try a shorter term or check for typos.</p>
            </div>
          ) : (
            <ul className="px-2 pb-2">
              {results.map((it, idx) => {
                const isActive = idx === active;
                return (
                  <li key={it.id}>
                    <div
                      data-idx={idx}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => { onOpenDetails(it); onClose(); }}
                      className={[
                        'group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
                        isActive ? 'bg-primary-soft' : 'hover:bg-light',
                      ].join(' ')}
                    >
                      {/* Thumb */}
                      <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                        <ProductImage
                          src={it.imageUrl}
                          name={it.name}
                          seed={it.id}
                          bgClassName={it.categoryTile}
                          compact="initials"
                        />
                      </div>
                      {/* Title + meta */}
                      <div className="flex-1 min-w-0 leading-tight">
                        <div className="flex items-center gap-2">
                          <p className="text-15 font-bold text-heading truncate">{it.name}</p>
                          {it.badge && (
                            <span className="inline-flex items-center text-10 font-bold uppercase tracking-wider px-1.5 h-5 rounded bg-danger text-[#FFFFFF] flex-shrink-0">
                              {it.badge}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-11 font-bold uppercase tracking-wider ${it.categoryEyebrow}`}>
                            {it.categoryLabel}
                          </span>
                          {it.description && (
                            <span className="text-12 font-medium text-gray-700 truncate">· {it.description}</span>
                          )}
                        </div>
                      </div>
                      {/* Price + actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-right leading-tight">
                          {it.originalPriceCents && (
                            <p className="text-11 text-gray-500 line-through tabular-nums">{fmtR(it.originalPriceCents)}</p>
                          )}
                          <p className="text-15 font-bold text-heading tabular-nums">{fmtR(it.priceCents)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onQuickAdd(it); onClose(); }}
                          title="Quick add (no options)"
                          aria-label="Quick add"
                          className="w-10 h-10 inline-flex items-center justify-center rounded-md bg-light border border-border text-heading hover:bg-success hover:border-success hover:text-[#FFFFFF] transition-colors"
                        >
                          <IconPlus size={16} stroke={2.5} />
                        </button>
                        <span className={[
                          'w-9 h-10 inline-flex items-center justify-center rounded-md transition-colors',
                          isActive
                            ? 'bg-primary text-[#FFFFFF]'
                            : 'bg-light text-gray-500 group-hover:bg-primary-soft group-hover:text-primary',
                        ].join(' ')}>
                          <IconArrowRight size={16} stroke={2.5} />
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Keyboard hints footer */}
        <div className="px-5 py-3 border-t border-border bg-light flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 text-12 font-semibold text-gray-700">
            <span className="inline-flex items-center gap-1">
              <Kbd><IconArrowUp size={11} stroke={2.5} /></Kbd>
              <Kbd><IconArrowDown size={11} stroke={2.5} /></Kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd><IconCornerDownLeft size={11} stroke={2.5} /></Kbd>
              <span className="ml-1">Open</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd><IconCommand size={11} stroke={2.5} /></Kbd>
              <Kbd><IconCornerDownLeft size={11} stroke={2.5} /></Kbd>
              <span className="ml-1">Quick add</span>
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-12 font-bold text-primary">
            <IconBolt size={12} stroke={2.5} fill="currentColor" />
            {items.length} products indexed
            <IconAdjustments size={12} stroke={2.5} className="ml-1 text-gray-500" />
          </span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded border border-border bg-surface text-11 font-bold text-heading">
      {children}
    </span>
  );
}
